// frontend/src/pages/model/CalculationsSection.jsx
// The Model page's Calculations panel (Phase 3 step 13c-3): the dataset's
// calculated columns and measures, each with its formula, what it gives, what
// uses it, and whether it runs. Admins add, edit, turn off and delete them; the
// editor is FormulaEditor. Deleting or turning off something that charts or
// other formulas use is refused by the server (CALCULATION_IN_USE) and a dialog
// asks before sending the request again forced. Everyone else reads the list.
//
// The list is fetched on its own (GET .../calculations) and reloaded after
// every change; onChanged tells the page to reload the field table too, since
// a formula's fields show what uses them.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Dialog } from 'radix-ui';
import { AlertTriangle, Calculator, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Panel, PanelHint } from '@/components/ui/panel.jsx';
import { checkCalculation, createCalculation, deleteCalculation, listCalculations, updateCalculation } from '../../charts/semanticApi';
import { CHARTS_SHOWN, chartHref, errorText, without } from './modelLogic';
import { KIND_LABELS, consequenceText, inUseTitleFor, replaceCalc, sortCalculations, statusOf, usageText } from './calcLogic';
import FormulaEditor from './FormulaEditor';
import Switch from './Switch';

const muted = 'text-[var(--ui-text-muted)]';
const IDLE = { status: 'idle', columns: [], measures: [], functions: [], limits: null, canEdit: false, error: null };

/** The status pill: what the formula gives, Broken, or Off. */
function StatusPill({ calc }) {
  const s = statusOf(calc);
  const cls = s.tone === 'bad' ? 'bg-red-500/15 text-red-300' : s.tone === 'off' ? 'bg-[var(--ui-chip)] text-[var(--ui-text-muted)]' : 'bg-[var(--ui-chip)] text-[var(--ui-text)]';
  return <span className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11.5px] ${cls}`}>{s.tone === 'bad' && <AlertTriangle className="h-3 w-3" aria-hidden="true" />}{s.text}</span>;
}

/** What uses the formula: a small disclosure with chart links and formula names. */
function UsedBy({ calc }) {
  const charts = Array.isArray(calc.used_by) ? calc.used_by : [];
  const formulas = Array.isArray(calc.used_by_calculations) ? calc.used_by_calculations : [];
  if (!charts.length && !formulas.length) return <span className={`text-[12.5px] ${muted}`}>Not used</span>;
  return (
    <details>
      <summary className="cursor-pointer select-none text-[12.5px] text-[var(--ui-text)] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30">{usageText(calc)}</summary>
      <ul className="mt-1.5 flex flex-col gap-1 text-[12px]">
        {charts.slice(0, CHARTS_SHOWN).map((c) => {
          const href = chartHref(c.id);
          const title = c.title || `Chart ${c.id}`;
          return <li key={`c${c.id}`} className="max-w-[220px] truncate">{href ? <Link to={href} className={`${muted} underline-offset-2 hover:text-[var(--ui-text)] hover:underline`}>{title}</Link> : title}</li>;
        })}
        {charts.length > CHARTS_SHOWN && <li className={muted}>and {charts.length - CHARTS_SHOWN} more charts</li>}
        {formulas.map((f) => <li key={f.key} className={`max-w-[220px] truncate ${muted}`}>{KIND_LABELS[f.kind]}: {f.display_name}</li>)}
      </ul>
    </details>
  );
}

/** One table of formulas (columns or measures). */
export function CalculationTable({ title, kind, items, canEdit, busy, errors, onNew, onEdit, onToggle, onDelete }) {
  return (
    <section className="mt-5 first:mt-0" aria-labelledby={`calc-${kind}-title`}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <h3 id={`calc-${kind}-title`} className="text-[13px] font-semibold text-[var(--ui-text)]">{title} <span className={`font-normal ${muted}`}>{items.length}</span></h3>
        {canEdit && (
          <Button variant="secondary" size="sm" onClick={() => onNew(kind)} className="rounded-full">
            <Plus className="h-3.5 w-3.5" aria-hidden="true" /> New {KIND_LABELS[kind].toLowerCase()}
          </Button>
        )}
      </div>
      {items.length === 0 ? (
        <p className={`rounded-xl border border-dashed border-[var(--ui-dash)] px-3 py-3 text-[12.5px] ${muted}`}>
          {kind === 'column' ? 'No calculated columns yet. A column is a formula per row, such as salary / 12, and works like any field.'
            : 'No measures yet. A measure is a formula over a group, such as sum(salary) / count(), and goes in a chart as a value.'}
        </p>
      ) : (
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full min-w-[760px] border-collapse text-left">
            <thead>
              <tr>
                {['Name', 'Formula', 'Gives', 'Used in', canEdit ? 'On' : null, canEdit ? '' : null].filter((h) => h !== null).map((h, i) => (
                  <th key={`${h}-${i}`} scope="col" className={`pb-2 pr-3 text-xs font-normal ${muted}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((c) => {
                const isBusy = Boolean(busy[c.key]);
                const err = errors[c.key];
                return (
                  <React.Fragment key={c.key}>
                    <tr aria-busy={isBusy || undefined} className={`border-t border-[var(--ui-divider)] align-top ${c.is_enabled ? '' : 'opacity-60'}`}>
                      <td className="py-2.5 pr-3">
                        <div className="text-[13.5px] text-[var(--ui-text)]">{c.display_name}</div>
                        <div className={`mt-0.5 flex items-center gap-1.5 text-[11px] ${muted}`}>
                          <span className="font-mono">{c.name}</span>
                          {c.scope === 'connector_template' && <span className="rounded bg-[var(--ui-chip)] px-1.5 text-[var(--ui-text)]">From the connector</span>}
                        </div>
                        {c.description && <div className={`mt-0.5 max-w-[320px] truncate text-[11.5px] ${muted}`} title={c.description}>{c.description}</div>}
                      </td>
                      <td className="py-2.5 pr-3">
                        <code className="block max-w-[360px] truncate font-mono text-[12px] text-[var(--ui-text)]" title={c.source}>{c.source}</code>
                        {c.error && <p className="mt-1 max-w-[360px] text-[11.5px] text-red-300">{c.error}</p>}
                      </td>
                      <td className="w-[130px] py-2.5 pr-3"><StatusPill calc={c} /></td>
                      <td className="py-2.5 pr-3"><UsedBy calc={c} /></td>
                      {canEdit && (
                        <td className="w-[60px] py-2.5 pr-3">
                          {c.scope === 'tenant' ? (
                            <Switch checked={c.is_enabled} disabled={isBusy} label={`${c.display_name} is on`} title={c.is_enabled ? 'On: offered in the builder' : 'Off: kept, not offered'}
                              onChange={(on) => onToggle(c, on)} />
                          ) : <span className={`text-[12.5px] ${muted}`}>—</span>}
                        </td>
                      )}
                      {canEdit && (
                        <td className="w-[90px] py-2.5">
                          {c.scope === 'tenant' ? (
                            <div className="flex items-center justify-end gap-1">
                              {isBusy && <Loader2 className={`h-3.5 w-3.5 animate-spin ${muted}`} role="status" aria-label="Saving" />}
                              <button type="button" onClick={() => onEdit(c)} disabled={isBusy} aria-label={`Edit ${c.display_name}`} title="Edit"
                                className={`rounded-lg p-1.5 ${muted} hover:bg-[var(--ui-inset-hover)] hover:text-[var(--ui-text)] disabled:opacity-50`}><Pencil className="h-3.5 w-3.5" aria-hidden="true" /></button>
                              <button type="button" onClick={() => onDelete(c)} disabled={isBusy} aria-label={`Delete ${c.display_name}`} title="Delete"
                                className={`rounded-lg p-1.5 ${muted} hover:bg-red-500/10 hover:text-red-300 disabled:opacity-50`}><Trash2 className="h-3.5 w-3.5" aria-hidden="true" /></button>
                            </div>
                          ) : null}
                        </td>
                      )}
                    </tr>
                    {err && (
                      <tr><td colSpan={canEdit ? 6 : 4} className="pb-2.5"><p role="alert" className="rounded-lg bg-red-500/10 px-3 py-1.5 text-xs text-red-300">{err}</p></td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** "Delete X?" or "X is used by ...": the confirmation before a delete or a forced change. request: { calc, action, charts, calculations } */
export function ConfirmContent({ request, busy, onCancel, onConfirm, cancelRef }) {
  const { calc, action, charts = [], calculations = [] } = request;
  const inUse = charts.length > 0 || calculations.length > 0;
  const title = inUse ? inUseTitleFor(calc.display_name, charts.length, calculations.length)
    : action === 'delete' ? `Delete ${calc.display_name}?` : `Turn off ${calc.display_name}?`;
  const body = inUse ? consequenceText(action)
    : action === 'delete' ? 'The formula is removed for good. Nothing uses it today.' : 'It is kept, but the builder stops offering it.';
  return (
    <>
      <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-amber-500/10"><AlertTriangle className="h-5 w-5 text-amber-500" aria-hidden="true" /></div>
      <Dialog.Title className="mb-2 text-lg font-semibold tracking-[-0.01em]">{title}</Dialog.Title>
      <Dialog.Description className={`mb-3 text-sm ${muted}`}>{body}</Dialog.Description>
      {inUse && (
        <ul className="mb-5 flex max-h-48 flex-col gap-1 overflow-y-auto rounded-xl bg-[var(--ui-inset)] p-3 text-[13px] scrollbar-thin">
          {charts.map((c) => { const href = chartHref(c.id); const t = c.title || `Chart ${c.id}`; return <li key={`c${c.id}`} className="truncate">Chart: {href ? <a href={href} target="_blank" rel="noopener noreferrer" className="underline-offset-2 hover:underline">{t}</a> : t}</li>; })}
          {calculations.map((c) => <li key={c.key} className="truncate">{KIND_LABELS[c.kind]}: {c.display_name}</li>)}
        </ul>
      )}
      <div className="flex items-center justify-end gap-3">
        <Button ref={cancelRef} variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="danger" onClick={onConfirm} disabled={busy}>
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
          {action === 'delete' ? (inUse ? 'Delete anyway' : 'Delete') : 'Turn off anyway'}
        </Button>
      </div>
    </>
  );
}

function ConfirmDialog({ request, busy, onCancel, onConfirm }) {
  const cancelRef = useRef(null);
  return (
    <Dialog.Root open={Boolean(request)} onOpenChange={(next) => { if (!next && !busy) onCancel(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content onOpenAutoFocus={(e) => { e.preventDefault(); if (cancelRef.current) cancelRef.current.focus(); }}
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-[22px] bg-[var(--ui-panel)] p-6 text-[var(--ui-text)] shadow-[0_24px_60px_rgba(0,0,0,0.6)] ring-1 ring-white/5 focus:outline-none">
          {request && <ConfirmContent request={request} busy={busy} onCancel={onCancel} onConfirm={onConfirm} cancelRef={cancelRef} />}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** datasetId: the selected dataset. fields: the model's fields (for the editor's insert list). onChanged(): a formula changed. */
export default function CalculationsSection({ datasetId, canEdit, fields, onChanged }) {
  const [state, setState] = useState(IDLE);
  const [reload, setReload] = useState(0);
  const [busy, setBusy] = useState({});
  const [errors, setErrors] = useState({});
  const [editor, setEditor] = useState(null);     // { mode, kind, current }
  const [confirm, setConfirm] = useState(null);   // { calc, action, charts, calculations }
  const idRef = useRef(null);
  idRef.current = datasetId;

  useEffect(() => {
    setBusy({}); setErrors({}); setEditor(null); setConfirm(null);
    if (!datasetId) { setState(IDLE); return undefined; }
    const ctrl = new AbortController();
    setState((s) => ({ ...s, status: 'loading', error: null }));
    listCalculations(datasetId, { signal: ctrl.signal })
      .then((body) => setState({
        status: 'ready', columns: sortCalculations(body.columns), measures: sortCalculations(body.measures),
        functions: body.functions || [], limits: body.limits || null, canEdit: body.can_edit === true, error: null,
      }))
      .catch((error) => { if (!ctrl.signal.aborted) setState({ ...IDLE, status: 'error', error }); });
    return () => ctrl.abort();
  }, [datasetId, reload]);

  const all = useMemo(() => [...state.columns, ...state.measures], [state.columns, state.measures]);
  const refresh = useCallback(() => { setReload((n) => n + 1); if (onChanged) onChanged(); }, [onChanged]);

  const put = (saved) => setState((s) => ({
    ...s,
    columns: saved.kind === 'column' ? sortCalculations(replaceCalc(s.columns, saved)) : s.columns,
    measures: saved.kind === 'measure' ? sortCalculations(replaceCalc(s.measures, saved)) : s.measures,
  }));

  /** A change to one row (toggle, delete), with the in-use confirmation on a 409. */
  const change = useCallback(async (calc, action, force = false) => {
    const id = idRef.current;
    if (!id) return;
    setBusy((b) => ({ ...b, [calc.key]: true }));
    setErrors((e) => without(e, calc.key));
    try {
      if (action === 'delete') await deleteCalculation(id, calc.key, { force });
      else put(await updateCalculation(id, calc.key, { is_enabled: action === 'enable', ...(force ? { force: true } : {}) }));
      if (idRef.current !== id) return;
      setConfirm(null);
      refresh();
    } catch (err) {
      if (idRef.current !== id) return;
      if (err && err.name === 'SemanticError' && err.code === 'CALCULATION_IN_USE') {
        setConfirm({ calc, action, charts: err.charts, calculations: err.calculations });
      } else {
        setConfirm(null);
        setErrors((e) => ({ ...e, [calc.key]: errorText(err) }));
      }
    } finally {
      if (idRef.current === id) setBusy((b) => without(b, calc.key));
    }
  }, [refresh]);

  const onToggle = (calc, on) => { if (on) change(calc, 'enable'); else change(calc, 'disable'); };
  const onDelete = (calc) => setConfirm({ calc, action: 'delete', charts: [], calculations: [] });
  const confirmBusy = Boolean(confirm && busy[confirm.calc.key]);

  const editing = canEdit && state.canEdit;
  const tableProps = { canEdit: editing, busy, errors, onNew: (kind) => setEditor({ mode: 'new', kind, current: null }), onEdit: (c) => setEditor({ mode: 'edit', kind: c.kind, current: c }), onToggle, onDelete };

  return (
    <Panel>
      <div className="mb-1 flex items-center gap-2">
        <Calculator className={`h-4 w-4 ${muted}`} aria-hidden="true" />
        <h2 className="text-sm font-semibold tracking-[-0.01em] text-[var(--ui-text)]">Calculations</h2>
      </div>
      <PanelHint className="mt-0">
        {editing
          ? 'Formulas over this dataset, written like a spreadsheet. A calculated column gives one value per row and behaves like a field; a measure gives one value per group and goes in a chart as a value. Saved formulas are live in the builder at once.'
          : 'Formulas over this dataset that the builder offers next to its fields. Only admins can change them.'}
      </PanelHint>
      {state.status === 'loading' && <p className={`mt-4 flex items-center gap-2 text-sm ${muted}`} role="status"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading calculations</p>}
      {state.status === 'error' && (
        <div className="mt-4 flex items-center gap-3 text-sm"><span className={muted}>{errorText(state.error)}</span><Button variant="secondary" size="sm" onClick={() => setReload((n) => n + 1)}>Try again</Button></div>
      )}
      {state.status === 'ready' && (
        <div className="mt-4">
          <CalculationTable title="Calculated columns" kind="column" items={state.columns} {...tableProps} />
          <CalculationTable title="Measures" kind="measure" items={state.measures} {...tableProps} />
        </div>
      )}

      <FormulaEditor
        request={editor} fields={fields} calculations={all} functions={state.functions}
        check={(body, opts) => checkCalculation(datasetId, body, opts)}
        save={(body, { key }) => (key ? updateCalculation(datasetId, key, body) : createCalculation(datasetId, body))}
        onSaved={(saved) => { put(saved); setEditor(null); refresh(); }}
        onCancel={() => setEditor(null)}
      />
      <ConfirmDialog request={confirm} busy={confirmBusy} onCancel={() => setConfirm(null)}
        // The first confirmation sends the plain request; only after the server listed what breaks is it forced.
        onConfirm={() => { if (confirm) change(confirm.calc, confirm.action, confirm.charts.length > 0 || confirm.calculations.length > 0); }} />
    </Panel>
  );
}
